import React, { useEffect, useRef, useState } from "react";
import "./updateServiceItemModal.css";
import {
  priceStrFormatter,
  removePriceStrFormatter,
} from "../../../../utils/formatters/priceStrFormatter";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import {
  PAY_BUCKET_LABELS,
  PAY_BUCKET_HELP,
  payBucketLabel,
  getOtherBaselineCents,
} from "../../../../utils/payBucketsClient";

const blankItem = {
  itemName: "",
  itemId: "",
  discountApplicable: "False",
  index: "",
  price: "",
  priceStr: "",
  jobType: "Refinish",
  // 2026-10-06: no default bucket — a new item's pay must be chosen. The
  // old 'refinish' default (plus the Job Type → bucket auto-suggest) is how
  // null and 'other' catalog items got silently rewritten on every save
  // (todo t_paybucket_form_rewrite_0927).
  payBucket: "",
  customPayCents: "",
  customPayStr: "",
  requiresPhotos: "True",
};

// Job Type is a LABEL (2026-10-06). It names the work on quotes, invoices,
// the calendar and reports; it no longer suggests or sets the pay bucket.
// The only place pay math still reads it is payCalc's fallback for legacy
// lines that carry no bucket at all.
const PAY_BUCKET_OPTIONS = ["refinish", "half", "full", "custom"];

// Typed text → cents string: digits only, no sign, no leading zeros. "" stays
// "" so a cleared Custom pay field means "blank = 100% of the price" — the
// shared priceStrFormatter would turn it into "0", i.e. $0.00 pay.
const centsFromTyped = (value) => {
  const digits = String(value ?? "").replace(/[^0-9]/g, "");
  if (digits === "") return "";
  return String(parseInt(digits, 10));
};

const UpdateServiceItemModal = ({
  close,
  selectedItemObj,
  open,
  updateServiceItem,
  createItem,
  createItemFunc,
}) => {
  const { handleSnack } = useSnackbar();
  const [changedItemObj, setChangedItemObj] = useState({ ...blankItem });
  const [errors, setErrors] = useState({});
  // True while a save is in flight — including while the parent's "this
  // changes the Maintenance Program price" confirm is open. That confirm is
  // portalled OUTSIDE this modal, so an Escape / backdrop click that reaches
  // the modal must not close it and wipe the edits (the
  // EmailTemplateEditorModal focus-trap race, sign-off F3 there).
  //
  // MERGE 2026-09-28 — READ THIS BEFORE CHANGING IT. main guarded that with
  // MUI's `disableEnforceFocus={submitting}`, but this modal is no longer a
  // MUI Dialog: it is BdrModal, whose Escape handler is bound at the DOCUMENT
  // level and would fire straight through the portalled confirm. The
  // equivalent guard here is `closeOnEsc`/`closeOnBackdrop` driven off the
  // same flag, below. Dropping those re-opens the exact bug main fixed.
  const [submitting, setSubmitting] = useState(false);
  const submitPendingRef = useRef(false);

  useEffect(() => {
    if (selectedItemObj && selectedItemObj !== undefined) {
      const priceStr = priceStrFormatter(selectedItemObj?.price);
      // Load EXACTLY what is stored (2026-10-06, todo
      // t_paybucket_form_rewrite_0927): an untouched field goes back
      // unchanged, so renaming an item or changing its price never rewrites
      // its pay. refinish / half / full / custom load as-is; null loads as
      // "" (the "Not set" option, captioned with the server's
      // effectivePayBucket); the retired 'other' loads as Custom with its
      // old (price − material baseline) amount prefilled so an unchanged
      // save keeps paying what it pays today.
      const stored = selectedItemObj?.payBucket ?? null;
      let incomingBucket = "";
      let incomingCustom = "";
      if (PAY_BUCKET_OPTIONS.includes(stored)) {
        incomingBucket = stored;
        if (stored === "custom" && selectedItemObj?.customPayCents != null) {
          incomingCustom = String(selectedItemObj.customPayCents);
        }
      } else if (stored === "other") {
        incomingBucket = "custom";
        if (selectedItemObj?.customPayCents != null) {
          incomingCustom = String(selectedItemObj.customPayCents);
        } else {
          const baseline = getOtherBaselineCents(selectedItemObj);
          if (baseline > 0) {
            const price = parseInt(selectedItemObj?.price) || 0;
            incomingCustom = String(Math.max(0, price - baseline));
          }
        }
      }
      // requiresPhotos is Boolean|null; null stays unselected ("" — treated
      // as required by the JobForm until someone picks one).
      const incomingRequiresPhotos =
        selectedItemObj?.requiresPhotos === true
          ? "True"
          : selectedItemObj?.requiresPhotos === false
            ? "False"
            : "";
      setChangedItemObj({
        ...selectedItemObj,
        priceStr: priceStr || "",
        itemId: selectedItemObj?.id,
        jobType: selectedItemObj?.jobType ?? "",
        discountApplicable: capitalizeFirstLetters(selectedItemObj?.discountApplicable?.toString()),
        payBucket: incomingBucket,
        customPayCents: incomingCustom,
        customPayStr: incomingCustom === "" ? "" : priceStrFormatter(incomingCustom),
        requiresPhotos: incomingRequiresPhotos,
      });
    }
  }, [selectedItemObj]);

  const clearItemObj = () => {
    setChangedItemObj({ ...blankItem });
  };

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    // Clear this field's error the moment it is touched — leaving a red
    // banner under a field the user is actively fixing reads as broken.
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

    if (name === "price") {
      const toNumStr = removePriceStrFormatter(value);
      const newPriceStr = priceStrFormatter(toNumStr);
      setChangedItemObj((prev) => ({
        ...prev,
        price: toNumStr,
        priceStr: newPriceStr,
      }));

      return;
    }

    // Custom pay per unit (2026-10-06): same digits-shift entry as Price,
    // but a cleared field stays blank (= 100% of the price), never "$0.00".
    if (name === "customPay") {
      const cents = centsFromTyped(value);
      setChangedItemObj((prev) => ({
        ...prev,
        customPayCents: cents,
        customPayStr: cents === "" ? "" : priceStrFormatter(cents),
      }));
      return;
    }

    // Job Type is a label: changing it touches nothing else (2026-10-06).
    // payBucket and requiresPhotos values are already canonical — don't
    // capitalize them. Only the legacy fields (jobType, discountApplicable)
    // run through capitalizeFirstLetters.
    if (name === "payBucket" || name === "requiresPhotos") {
      setChangedItemObj((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }

    value = capitalizeFirstLetters(value);

    setChangedItemObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Per-field validation messages, keyed by field name so each one can light
  // up its own input.
  const validate = () => {
    const next = {};
    const { itemName, price } = changedItemObj;

    if (typeof itemName !== "string" || itemName.trim().length === 0) {
      next.itemName = "Enter an item name.";
    }

    if (typeof price !== "string" || price.length === 0) {
      next.price = "Enter a price.";
    }

    return next;
  };

  const handleClose = () => {
    /*
     * MERGE 2026-09-28: this used to read MUI's `(event, reason)` and bail on
     * "escapeKeyDown" / "backdropClick". BdrModal calls onClose with NO
     * arguments, so that guard was inert — `reason` was always undefined and
     * the modal would have closed mid-save after all. The real protection is
     * closeOnEsc/closeOnBackdrop below; this is the belt to their braces, and
     * it also covers the × and Cancel.
     */
    if (submitPendingRef.current) return;
    clearItemObj();
    setErrors({});
    close();
  };

  /*
   * MERGE 2026-09-28. Git left TWO `handleSubmit` definitions here — adjacent
   * rather than overlapping, so it reported no conflict, but a `const`
   * redeclaration is a hard SyntaxError. This is the two combined.
   *
   * main's structure wins, because it carries the behaviour ours lacked: it
   * AWAITS the parent's save and only closes when that returns true. Ours fired
   * the save without awaiting and closed unconditionally, which discards the
   * user's edits when the save fails or the price confirm is cancelled.
   *
   * Ours contributes per-field validation: main called `updateCheck()`, which
   * this file renamed to `validate()` and which now returns a map of messages
   * rather than a boolean. Both cues are kept — the fields light up AND the
   * snackbar fires, which is what the rest of this file already does.
   */
  const handleSubmit = async () => {
    if (submitPendingRef.current) return;

    const found = validate();
    setErrors(found);

    // STAY OPEN on failure. This used to clear the form and close regardless
    // of the result, so submitting an empty Create silently discarded
    // everything typed with no message.
    if (Object.keys(found).length > 0) {
      handleSnack("Item name and price are required", 3000);
      return;
    }
    // A bucket must be chosen, except that an item stored with no bucket may
    // be saved as "Not set" (it keeps paying through the server's fallback)
    // so an unrelated edit never forces a pay decision (2026-10-06).
    const canKeepNotSet = !createItem && (selectedItemObj?.payBucket ?? null) == null;
    if (!changedItemObj?.payBucket && !canKeepNotSet) {
      handleSnack("Pick a pay bucket", 3000);
      return;
    }
    if (
      changedItemObj?.payBucket === "custom" &&
      changedItemObj?.customPayCents !== "" &&
      !/^\d+$/.test(changedItemObj?.customPayCents ?? "")
    ) {
      handleSnack("Custom pay must be a dollar amount, or blank for 100% of the price", 3000);
      return;
    }
    const payload = {
      ...changedItemObj,
      jobType: changedItemObj?.jobType || null,
      discountApplicable:
        changedItemObj?.discountApplicable === "True" ? true : false,
      // "" (never picked) stays null — the JobForm treats null as required.
      requiresPhotos:
        changedItemObj?.requiresPhotos === "True"
          ? true
          : changedItemObj?.requiresPhotos === "False"
            ? false
            : null,
      // "" = Not set → null. customPayCents only means something on Custom;
      // blank → null (= 100% of the price on the server).
      payBucket: changedItemObj?.payBucket || null,
      customPayCents:
        changedItemObj?.payBucket === "custom" && changedItemObj?.customPayCents !== ""
          ? changedItemObj.customPayCents
          : null,
    };
    submitPendingRef.current = true;
    setSubmitting(true);
    let saved = false;
    try {
      // Let the closeOnEsc/closeOnBackdrop guard commit before a confirm can
      // open. Same race main hit with disableEnforceFocus: without the tick the
      // modal is still dismissible at the instant the confirm appears.
      await new Promise((resolve) => setTimeout(resolve, 0));
      saved = createItem
        ? await createItemFunc(payload)
        : await updateServiceItem(payload);
    } finally {
      submitPendingRef.current = false;
      setSubmitting(false);
    }
    if (saved === true) {
      clearItemObj();
      setErrors({});
      close();
    }
  };

  return (
    <BdrModal
      open={open}
      onClose={handleClose}
      title={createItem ? "Create Item" : "Update Item"}
      variant="panel"
      /* lg, not md: the Job Type row has six radio options and Pay Bucket
         four. At 640 they wrap into a ragged block. */
      size="lg"
      /* main's submit guard, in BdrModal's vocabulary — see the note on
         `submitting` above. All three together, or the modal only LOOKS
         protected while a save is in flight. */
      closeOnEsc={!submitting}
      closeOnBackdrop={!submitting}
      showClose={!submitting}
      actions={
        <>
          <BdrButton disabled={submitting} onClick={handleClose}>
            Cancel
          </BdrButton>
          {/* solid: this modal sits on the saturated --bdr-grad-panel */}
          <BdrButton
            variant="confirm-solid"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? "Saving..." : createItem ? "Create" : "Update"}
          </BdrButton>
        </>
      }
    >
      <div className="ser-man-modal-content bdr-dark-placeholder">
        <BdrInput
          label={
            createItem ? "Name" : `Name (prev: ${selectedItemObj?.itemName})`
          }
          name="itemName"
          error={errors.itemName}
          value={changedItemObj?.itemName}
          onChange={handleChange}
          placeholder="Item name..."
        />

        {/* 2026-05-15b reorg — paired the two Yes/No toggles (Apply
            Discount + Photos Required) on a single row, then gave Job
            Type and Pay Bucket their own full-width rows so the longer
            radio groups have room to spread out. Before this, Job Type
            (6 options) was crammed into a half-width column alongside
            Apply Discount and overflowed horizontally. */}
        <div className="ser-man-fields-row">
          <div className="bdr-field">
            <span className="bdr-field-label">Apply Discount</span>
            <div className="bdr-radio-row">
              <label>
                <input
                  type="radio"
                  name="discountApplicable"
                  value="True"
                  checked={changedItemObj?.discountApplicable === "True"}
                  onChange={handleChange}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="discountApplicable"
                  value="False"
                  checked={changedItemObj?.discountApplicable === "False"}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
          </div>

          <div className="bdr-field">
            <span className="bdr-field-label">Photos Required</span>
            <div className="bdr-radio-row">
              <label>
                <input
                  type="radio"
                  name="requiresPhotos"
                  value="True"
                  checked={changedItemObj?.requiresPhotos === "True"}
                  onChange={handleChange}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="requiresPhotos"
                  value="False"
                  checked={changedItemObj?.requiresPhotos === "False"}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
            <span className="bdr-field-helper">
              When any item on an invoice requires photos, the JobForm
              enforces them.{" "}
              {changedItemObj?.requiresPhotos === ""
                ? "Not set on this item — treated as required until you pick one."
                : "Parts, trip charges and tips usually don't need photos."}
            </span>
          </div>
        </div>

        <div className="bdr-field">
          <span className="bdr-field-label">Job Type</span>
          <div className="bdr-radio-row">
            <label>
              <input
                type="radio"
                name="jobType"
                value="Refinish"
                checked={changedItemObj?.jobType === "Refinish"}
                onChange={handleChange}
              />
              Refinish
            </label>
            <label>
              <input
                type="radio"
                name="jobType"
                value="Maintenance"
                checked={changedItemObj?.jobType === "Maintenance"}
                onChange={handleChange}
              />
              Maintenance
            </label>
            <label>
              <input
                type="radio"
                name="jobType"
                value="Hardware Change"
                checked={changedItemObj?.jobType === "Hardware Change"}
                onChange={handleChange}
              />
              Hardware Change
            </label>
            <label>
              <input
                type="radio"
                name="jobType"
                value="Trip Charge"
                checked={changedItemObj?.jobType === "Trip Charge"}
                onChange={handleChange}
              />
              Trip Charge
            </label>
            <label>
              <input
                type="radio"
                name="jobType"
                value="Other"
                checked={changedItemObj?.jobType === "Other"}
                onChange={handleChange}
              />
              Other
            </label>
            <label>
              <input
                type="radio"
                name="jobType"
                value="Tip"
                checked={changedItemObj?.jobType === "Tip"}
                onChange={handleChange}
              />
              Tip
            </label>
          </div>
          <span className="bdr-field-helper">
            A label only — it names the work on quotes, invoices, the
            calendar and reports. Pay comes from the Pay Bucket below.
          </span>
        </div>

        <div className="bdr-field">
          <span className="bdr-field-label">Pay Bucket</span>
          <div className="bdr-radio-row">
            {PAY_BUCKET_OPTIONS.map((bucket) => (
              <label key={bucket} title={PAY_BUCKET_HELP[bucket]}>
                <input
                  type="radio"
                  name="payBucket"
                  value={bucket}
                  checked={changedItemObj?.payBucket === bucket}
                  onChange={handleChange}
                />
                {PAY_BUCKET_LABELS[bucket]}
              </label>
            ))}
            {/* Only an item stored with NO bucket can stay that way — it
                keeps paying through the server's legacy fallback, which is
                what the caption shows. Picking a bucket is a one-way door. */}
            {!createItem && (selectedItemObj?.payBucket ?? null) == null && (
              <label title="Keeps the item unassigned; pay keeps coming from the legacy Job Type / name rules">
                <input
                  type="radio"
                  name="payBucket"
                  value=""
                  checked={changedItemObj?.payBucket === ""}
                  onChange={handleChange}
                />
                {`Not set (auto: pays as ${payBucketLabel(selectedItemObj?.effectivePayBucket)})`}
              </label>
            )}
          </div>
          <span className="bdr-field-helper">
            Decides how the line price becomes refinisher pay. Snapshotted
            onto each quote / invoice line when it is added, so changing it
            here only affects jobs created from now on.
          </span>
          {changedItemObj?.payBucket === "custom" && (
            <BdrInput
              label="Custom pay per unit"
              name="customPay"
              inputMode="numeric"
              value={changedItemObj?.customPayStr}
              onChange={handleChange}
              placeholder="Blank = 100% of the price"
              helper={
                <>
                  Paid flat to the refinisher for each unit: no discount, no
                  percentage, split across the crew like every bucket. Leave it
                  blank to pay the full price.
                  {selectedItemObj?.payBucket === "other" &&
                    " This item used the retired Other formula (price minus material cost); that amount is prefilled so an unchanged save keeps paying the same."}
                </>
              }
            />
          )}
        </div>

        <BdrInput
          label={
            createItem
              ? "Price"
              : `Price (prev: ${priceStrFormatter(selectedItemObj?.price)})`
          }
          name="price"
          error={errors.price}
          value={changedItemObj?.priceStr}
          onChange={handleChange}
          placeholder="$0.00"
        />

      </div>
    </BdrModal>
  );
};

export default UpdateServiceItemModal;
